import { useEffect, useState } from "react";
import { useAtom } from "jotai";
import { PlatformProvider } from "@NBCUDTC/reactjs-utility-belt/components/contexts/PlatformContext";
import "@/assets/iconFont/streaming-icons.css";
import { logger } from "@NBCUDTC/reactjs-utility-belt/logger";

import { Mobile } from "@/components/ecosystems/Mobile";
import { platformAtom } from "@/constants/atoms";
import { Platform } from "@/types";
import { PageData } from "@/utils/FetchPageData/types";
import { fetchAirtableRecord } from "@/utils/airtable/FetchAirtableRecord";
import { fetchPageData } from "@/utils/FetchPageData";
import { AirtableTableId } from "@/utils/airtable/types";
import { Tile34 } from "@/components/molecules/Tile34";
import { TileMetadataBlock } from "@/components/molecules/TileMetadataBlock";
import { Tile169Size } from "@/components/molecules/Tile169/types";
import { Rail } from "@/components/organisms/Rail";
import { StreamingIcons } from "@/assets/iconFont/streaming-icons";
import { fetchAssetDetails } from "@/utils/FetchAssetDetails";
import { isSeries } from "@/utils/FetchAssetDetails/types";
import { fetchSeasonDetails } from "@/utils/FetchSeasonDetails";
import { fetchEpisodeDetails } from "@/utils/FetchEpisodeDetails";
import { getTileFormat } from "@/utils/GetTileFormat";

import styles from "./App.module.scss";

function App() {
  const [page, setPage] = useState<PageData>();
  const [platform, setPlatform] = useAtom(platformAtom);

  useEffect(() => {
    const envPlatform = process.env.PLATFORM || Platform.TV;
    const platformMap: Record<string, Platform> = {
      tv: Platform.TV,
      web: Platform.Web,
      mobile: Platform.Mobile,
    };
    setPlatform(platformMap[envPlatform] || Platform.TV);
  }, [setPlatform]);

  // Fetch Page Data
  useEffect(() => {
    const fetchAirtable = async () => {
      const pageId: string = "recdTLyT0Ljl22PDQ";
      const pageData = await Promise.resolve(
        fetchAirtableRecord(`${AirtableTableId.Pages}/${pageId}`),
      );
      const page = await Promise.resolve(fetchPageData(pageData));
      setPage(page);
    };

    fetchAirtable();
  }, []);

  // Test Series API
  useEffect(() => {
    (async () => {
      const seriesId = "cea8f5c4-0622-11ea-a306-9b6e77a98b2c"; // Below Deck Mediterranean

      try {
        // 1. Fetch series data
        logger.info("Fetching series data...", { seriesId });
        const asset = await fetchAssetDetails(seriesId);

        // 2. Fetch seasons
        logger.info("Fetching seasons...");
        const seasons = await fetchSeasonDetails(
          isSeries(asset) ? asset.attributes.providerSeriesId : "",
        );
        logger.success("Seasons Data", { count: seasons.length, seasons });

        // 3. Fetch episodes for first 2 seasons as example
        if (seasons.length > 0) {
          logger.info("Fetching episodes for first 2 seasons...");

          // Season 1
          const season1Episodes = await fetchEpisodeDetails(seasons[0].id);
          logger.success(
            `Season 1 Episodes (${season1Episodes.length} total)`,
            season1Episodes,
          );
        }

        logger.success("Series API test complete!");
        logger.info("Summary", {
          title: asset.attributes.title,
          seasonsRetrieved: seasons.length,
          totalEpisodes: isSeries(asset)
            ? asset.attributes.availableEpisodeCount
            : 0,
        });
      } catch (error) {
        logger.error("Series API test failed:", error);
      }
    })();
  }, []);

  return (
    <PlatformProvider value={platform}>
      <div className={styles["app"]}>
        {platform === Platform.Mobile ? (
          <Mobile />
        ) : (
          <div className={styles["app-content"]}>
            {page?.rails.map((rail) => (
              <Rail
                key={rail.self.id}
                title={rail.self.fields.Name}
                icon={StreamingIcons.Lock}
              >
                {rail.assets &&
                  rail.assets.map((asset) => (
                    <Tile34
                      key={asset.id}
                      format={getTileFormat(asset)}
                      asset={asset}
                      locked={false}
                    />
                  ))}
              </Rail>
            ))}

            {page?.rails[0].assets && (
              <div style={{ background: "gray" }}>
                <TileMetadataBlock
                  asset={page.rails[0].assets[0]}
                  tileSize={Tile169Size.Medium}
                  progress={25}
                />
                <TileMetadataBlock
                  asset={page.rails[0].assets[1]}
                  tileSize={Tile169Size.Large}
                  progress={50}
                />
                <TileMetadataBlock
                  asset={page.rails[0].assets[2]}
                  tileSize={Tile169Size.XLarge}
                  progress={75}
                />
                <TileMetadataBlock
                  asset={page.rails[0].assets[3]}
                  tileSize={Tile169Size.XLarge}
                />
                <TileMetadataBlock
                  asset={page.rails[0].assets[4]}
                  tileSize={Tile169Size.XLarge}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </PlatformProvider>
  );
}

export { App };
